import { GENERATE_HASH_IGNORE_KEYS_FOR_LEGS } from "@/config/otp";
import { generateHash } from "@/lib/hash";
import { validateSchemaSafe } from "@/services/zod/validator";
import { AppItineraryOrNode, ItineraryCategories, ItineraryOrNode, ItinerarySchemaExtended, LegSchemaExtended, PickupInformationExtended, PlanResponseExtended } from "@/types/otp/+extended";
import { PlanConnectionVariables } from "@/types/otp/plan-connection.graphql";
import { OtpDate, OtpTime } from "@/types/otp/plan.graphql";
import { z } from "zod";
import { pickupInformationSchemaExtended } from "../validations/+extended";
import { planConnectionGraphqlResponse, PlanConnectionLeg, PlanConnectionNode, QueryName } from "../validations/plan-connection.graphql";
import { generateGraphqlQuery } from "./generate-otp-query.graphql";
import { addSeconds } from "date-fns";
import { otpGraphql } from "..";

export function generatePlanConnectionQuery<
  TQueryString extends string = string,
  TQueryName extends QueryName<TQueryString> = QueryName<TQueryString>,
>(
  query: TQueryString,
  name: TQueryName,
  url: string,
) {
  const q = generateGraphqlQuery<
    PlanConnectionVariables,
    TQueryString,
    TQueryName,
    typeof planConnectionGraphqlResponse
  >(
    query,
    name,
    url,
    planConnectionGraphqlResponse
  );

  return async (props?: PlanConnectionVariables) => {
    const res = await q(props);
    const extendedRes = middleware(res, props)
    return extendedRes
  };
}

function middleware(res: z.infer<typeof planConnectionGraphqlResponse>, props?: PlanConnectionVariables) {
  const itineraries = filterItineraries(res, props);
  const { data, ...rest } = res

  return {
    ...rest,
    data: {
      ...data,
      plan: {
        itineraries: data?.planConnection?.edges?.map(e => e.node),
      },
      extended: {
        itineraries
      }
    } satisfies PlanResponseExtended
  }
}

/**
 * Filters and removes itineraries based on specific query parameters.
 *
 * MIDDLEWARE IS USED SINCE OTP CURRENTLY DOES NOT SUPPORT SOME PARAMETERS WITH FLEX ROUTES
 *
 * @param props
 * @param res
 */
function filterItineraries(res: z.infer<typeof planConnectionGraphqlResponse>, props?: PlanConnectionVariables): ItineraryCategories | undefined {
  if (!res.data?.planConnection?.edges || !props) {
    return;
  }

  const isMedicalTransport = props.transportType === "m";
  const bannedRoutes = isMedicalTransport && otpGraphql.routesCache?.standardIds
    ? otpGraphql.routesCache.standardIds.split(",")
    : [];
  const bannedAgencies: string[] = [] //props.banned?.agencies?.split(",")
  const bannedTrips: string[] = [] //props.banned?.agencies?.split(",")
  const searchWindow = getSearchWindow(props)

  const seenHashes: Set<string> = new Set()
  const seenFutureHashes: Set<string> = new Set()

  const all: AppItineraryOrNode[] = [] satisfies AppItineraryOrNode[]
  const valid: ItinerarySchemaExtended[] = [] satisfies ItinerarySchemaExtended[]
  const future: ItineraryOrNode[] = [] satisfies ItineraryOrNode[]
  const filtered: ItineraryOrNode[] = [] satisfies ItineraryOrNode[]
  for (const itinerary of res.data.planConnection.edges) {
    if (!itinerary.node) {
      all.push(itinerary.node)
      filtered.push(itinerary.node)
      continue;
    }

    const routeIds: string[] = [];
    let containsOnlyWalking = true;
    const legMatchesFilter = itinerary?.node?.legs?.find((l) => {
      containsOnlyWalking &&= l.mode === 'WALK';
      if (l.route?.gtfsId) {
        routeIds.push(l.route?.gtfsId);
      }

      return (
        l.route?.gtfsId && bannedRoutes && bannedRoutes.includes(l.route.gtfsId) ||
        l.agency?.gtfsId && bannedAgencies && bannedAgencies.includes(l.agency.gtfsId) ||
        l.trip?.gtfsId && bannedTrips && bannedTrips.includes(l.trip.gtfsId)
      );
    })

    if (legMatchesFilter ||
      containsOnlyWalking
      // containsOnlyWalking ||
      // (typeof props.maxRouteCountMiddleware === "number" && routeIds.length > props.maxRouteCountMiddleware)
    ) {
      all.push(itinerary.node)
      filtered.push(itinerary.node)
      continue;
    }

    const legHash = generateHash(itinerary.node.legs, { ignore: GENERATE_HASH_IGNORE_KEYS_FOR_LEGS })
    const isFuture = isFutureItinerary(itinerary.node, props, searchWindow);
    if (isFuture && seenFutureHashes.has(legHash)) {
      all.push(itinerary.node)
      filtered.push(itinerary.node)
      continue;
    }

    if (isFuture && !seenFutureHashes.has(legHash)) {
      seenFutureHashes.add(legHash)
      all.push(itinerary.node)
      future.push(itinerary.node)
      continue;
    }

    if (seenHashes.has(legHash)) {
      all.push(itinerary.node)
      filtered.push(itinerary.node)
      continue;
    }

    seenHashes.add(legHash)

    const extended = extendItinerary(itinerary.node);
    if (extended) {
      all.push(extended)
      valid.push(extended)
    } else {
      all.push(itinerary.node)
      filtered.push(itinerary.node)
    }
  }

  return {
    valid,
    filtered,
    future,
    all: all,
  } satisfies ItineraryCategories
}

function isFutureItinerary(itinerary: PlanConnectionNode, props?: PlanConnectionVariables, searchWindow?: Date) {
  if (!props) {
    return;
  }

  if (props.dateTime && "latestArrival" in props.dateTime) {
    return itinerary.endTime && searchWindow && new Date(itinerary.endTime) > searchWindow
  }

  return itinerary.startTime && searchWindow && new Date(itinerary.startTime) > searchWindow
}

function getSearchWindow(props?: PlanConnectionVariables) {
  if (!props || !props.searchWindowMiddleware) {
    return undefined;
  }

  const startTime =
  !props.dateTime
    ? new Date()
    : "latestArrival" in props.dateTime
      ? props.dateTime.latestArrival
      : props.dateTime.earliestDeparture;

  const queryStartTime = addSeconds(startTime, props.searchWindowMiddleware ?? 0);

  return queryStartTime;
}

export function getSearchWindowStartTime(date?: OtpDate, time?: OtpTime, now = new Date()) {
  if (date && time) {
    return new Date(`${date} ${time}`)
  }

  if (date) {
    return new Date(`${date}`)
  }

  if (time) {
    return new Date(`${now.toDateString()} ${date}`)
  }

  return new Date()
}

function extendItinerary(i: PlanConnectionNode): ItinerarySchemaExtended | undefined {
  if (!i.legs || i.legs.length <= 0) {
    return undefined;
  }

  const legs: LegSchemaExtended[] = [];
  for (const leg of i.legs) {
    const agencyInformation = tryGetPickupInformation(leg)

    legs.push({
      ...leg,
      extended: {
        pickupInformation: agencyInformation
      }
    })
  }

  return {
    ...i,
    legs: legs
  };
}

function tryGetPickupInformation(leg: PlanConnectionLeg): PickupInformationExtended | undefined {
  const messageToParse = leg.pickupBookingInfo?.pickupMessage ?? leg.dropOffBookingInfo?.pickupMessage
  if (!messageToParse) {
    return;
  }

  return validateSchemaSafe({
    dto: JSON.parse(messageToParse),
    schema: pickupInformationSchemaExtended,
    schemaName: "PICKUP INFORMATION EXTENDED",
  }, {
    messagePrefix: "Pickup Information Validation Error:"
  });
}
